import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Sparkles, CheckCircle2, FileText } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";


// ---------------------------------------------------------------------------
// Helper insurance — title, blurb, image, option dropdowns, price, apply.
//
// Which dropdowns exist and what they contain comes from the server
// (backend/routes/helper_insurance.py); the price is looked up there too,
// and Apply sends the selections only — the server re-resolves the price
// and writes the vendor_purchases record itself. Nothing is computed here.
// ---------------------------------------------------------------------------

const IMAGE_SRC = "/images/helper-insurance.jpg"; // frontend/public/images/

const money = (n) =>
    Number(n || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");
const today = new Date();
today.setHours(0, 0, 0, 0);

export default function HelperInsurance() {
    const [options, setOptions] = useState(null);
    const [optionsError, setOptionsError] = useState(null);

    const [selections, setSelections] = useState({});
    const [price, setPrice] = useState(null);
    const [pricing, setPricing] = useState(false);
    const [priceError, setPriceError] = useState(null);

    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(null);

    // inside the component
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dealIdParam = searchParams.get("deal");

    const [dealImage, setDealImage] = useState(null);

    useEffect(() => {
        if (!dealIdParam) return;
        api.get(`/public/deals/${dealIdParam}`)
            .then((r) => setDealImage(r.data?.image || null))
            .catch(() => setDealImage(null));
    }, [dealIdParam]);

    const imageSrc = dealImage || IMAGE_SRC;

    useEffect(() => {
        api.get("/member/helper-insurance/options")
            .then((r) => setOptions(r.data))
            .catch((e) => setOptionsError(formatApiError(e)));
    }, []);

    const complete = useMemo(
        () => Boolean(options) && options.fields.every((f) => selections[f.key]),
        [options, selections]
    );

    //used for the duration, i'm just hard coding it
    const PLAN_ORDER = ["1 Year Plan", "2 Years Plan"];

    // The plan dropdown is limited to the two terms we sell. Values absent
    // from the rate data are dropped rather than offered, so the member
    // can't pick a plan we have no price for.
    const visibleValues = (f) =>
        f.key === "Helper Plan"
            ? PLAN_ORDER.filter((p) => f.values.includes(p))
            : f.values;


    // Plan documents, served statically from frontend/public/pdf/.
    const PLAN_DOCS = [
        { key:"MSIG Enhanced Plan", title: "Standard - MSIG", file: "/pdf/MSIG_Domestic_Helper_Insurance_Brochure_Mar_2026.pdf", caption: "PDF · Everyday protection for your helper" },
        { key:"ALLIED WORLD Maidguard Plan III", title: "Premium - Allied", file: "/pdf/Allied World_MaidGuard_Plan_3_20230328.pdf", caption: "PDF · policy details" },
        { key:"ZURICH Helpersafe Plan",  title: "Elite - ZURICH", file: "/pdf/Zurich Helper Insurance.pdf", caption: "PDF · Bigger cover, bigger peace of mind" },
    ];

    const PLAN_DOC_LOOKUP = Object.fromEntries(
        PLAN_DOCS.map((plan) => [plan.key, plan])
    );

    // consts used for the data entry form
    const EMPTY_PARTY = {
        salutation: "", first_name: "", last_name: "", hkid: "",
        hk_card_no: "", passport_no: "", nationality: "",
        telephone: "", email: "", occupation: "", residential_address: "",
        date_of_birth: "",
    };

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const HKID_RE = /^[A-Z]{1,2}\d{6}\(\d\)$/i;

    const [applicant, setApplicant] = useState(EMPTY_PARTY);
    const [helper, setHelper] = useState(EMPTY_PARTY);
    const [insurance, setInsurance] = useState({ contract_start_date: "", insurance_start_date: "" });
    const [declined, setDeclined] = useState(null);   // null | "yes" | "no"
    const [declinedDetails, setDeclinedDetails] = useState("");
    const [claimed, setClaimed] = useState(null);
    const [claimedDetails, setClaimedDetails] = useState("");
    const [errors, setErrors] = useState({});
    const [understand, setUnderstand] = useState(null); // null | "yes" | "no"


    // Fetch the price as soon as every dropdown has a value. Any change to
    // the selections also clears a previous submission so a new quote can be
    // applied for.
    useEffect(() => {
        setSubmitted(null);
        if (!complete) {
            setPrice(null);
            return undefined;
        }
        let cancelled = false;
        setPricing(true);
        api.post("/member/helper-insurance/price", { selections })
            .then((r) => {
                if (!cancelled) {
                    setPrice(r.data);
                    setPriceError(null);
                }
            })
            .catch((e) => {
                if (!cancelled) setPriceError(formatApiError(e));
            })
            .finally(() => {
                if (!cancelled) setPricing(false);
            });
        return () => {
            cancelled = true;
        };
    }, [complete, selections]);

    const validate = () => {
        const e = {};

        const require = (obj, key, label, prefix) => {
            if (!String(obj[key] || "").trim()) e[prefix + key] = `${label} is required`;
        };

        // Applicant / employer
        [
            "salutation", "first_name", "last_name", "hkid",
            "telephone", "email", "occupation", "residential_address",
        ].forEach((k) => require(applicant, k, APPLICANT_LABELS[k], "a_"));
        if (applicant.email && !EMAIL_RE.test(applicant.email)) {
            e.a_email = "Enter a valid email address";
        }
        if (applicant.hkid && !HKID_RE.test(applicant.hkid)) {
            e.a_hkid = "Format like A123456(7)";
        }

        // Helper
        [
            "salutation", "first_name", "last_name", "nationality",
            "hk_card_no", "passport_no", "date_of_birth",
        ].forEach((k) => require(helper, k, HELPER_LABELS[k], "h_"));

        // Insurance date
        require(insurance, "contract_start_date", "Contract of employment start date", "i_");
        require(insurance, "insurance_start_date", "Contract of insurance start date", "i_");

        if (insurance.insurance_start_date <= today)
        {
            e.insurance = "Insurance start date cannot be earlier than today";
        }

        // Declared history
        if (declined == null) e.declined = "Please answer this question";
        if (declined === "yes" && !declinedDetails.trim()) {
            e.declined_details = "Please give full details";
        }
        if (claimed == null) e.claimed = "Please answer this question";
        if (claimed === "yes" && !claimedDetails.trim()) {
            e.claimed_details = "Please give full details";
        }

        if(understand == null) e.understand = "Please answer this question";
        if(understand === "no"){
            e.understand = "You must understand and accept this condition";
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async () => {
        if (!validate()) {
            toast.error("Please complete all required fields.");
            // Bring the first offending field into view rather than leaving the
            // member to hunt for the red text.
            requestAnimationFrame(() => {
                document
                    .querySelector("[data-error='true']")
                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
            });
            return;
        }

        // null out the helper email because the back end python uses a common class for applicant and helper
        //helper.email = null;

        setBusy(true);
        try {
            const r = await api.post("/member/helper-insurance/submit", {
                selections,
                deal_id: dealIdParam,
                application: {
                    applicant,
                    helper,
                    insurance,
                    declined: declined === "yes",
                    declined_details: declined === "yes" ? declinedDetails.trim() : null,
                    claimed: claimed === "yes",
                    claimed_details: claimed === "yes" ? claimedDetails.trim() : null,
                },
            });

            setSubmitted(r.data);

            if (dealIdParam) {
                toast.success(`Request ${r.data.display_id} sent.`);
                navigate(`/member/deals/${dealIdParam}?resume=helper_calculator`);
                return;
            }
            toast.success(
                `Request ${r.data.display_id} sent — the insurer will be in touch to complete your cover.`
            );
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const header = (
        <PageHeader eyebrow="Insurance" title="Helper insurance" testid="helper-header" />
    );

    if (optionsError) {
        return (
            <>
                {header}
                <div className="px-6 lg:px-12 py-8">
                    <div className="rounded-2xl bg-white border border-red-200 p-6 text-sm text-navy-700">
                        <div className="font-semibold text-red-600 mb-1">
                            This page couldn&rsquo;t load.
                        </div>
                        {optionsError}
                    </div>
                </div>
            </>
        );
    }

    if (!options) {
        return (
            <>
                {header}
                <div className="px-6 lg:px-12 py-16 flex justify-center">
                    <Loader2 className="animate-spin text-navy-300" size={28} />
                </div>
            </>
        );
    }

    const { info, fields, vendor } = options;
    const APPLICANT_LABELS = {
        salutation: "Salutation", first_name: "First name", last_name: "Last name",
        hkid: "HKID Card No.", telephone: "Telephone", email: "Email address",
        occupation: "Occupation", residential_address: "Residential address",
    };
    const HELPER_LABELS = {
        salutation: "Salutation", first_name: "First name", last_name: "Last name",
        nationality: "Nationality", hk_card_no: "HK Card No.", passport_no: "Passport No.",
        date_of_birth: "Date of birth",
    };


    return (
        <>
            {header}

            <div className="px-6 lg:px-12 py-8 space-y-6 max-w-5xl">
                {/* ---------- Intro: text + image ---------- */}
                <div className="grid lg:grid-cols-2 gap-6 items-stretch">
                    <Card>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-gold-600">
                            Your helper's safety. Your peace of mind.
                        </div>
                        <h2 className="font-display text-xl text-navy-700 mt-1.5">
                            {info.plan}
                        </h2>
                        <p className="text-sm text-navy-600 mt-3 leading-relaxed line-clamp-3">
                           "Your helper keeps your world running, so this plan keeps them protected with instant, affordable, big‑hearted coverage that handles medical bills, accidents, hospital stays, dental needs and all the unexpected moments that can disrupt your home.
                        </p>
                    </Card>
                    <div className="rounded-2xl overflow-hidden shadow-soft border border-navy-100/60 h-[180px]">
                        <img
                            src={imageSrc}
                            alt={info.product || "Helper insurance"}
                            className="w-full object-cover"
                            data-testid="helper-image"
                        />
                    </div>
                </div>

                {/* ---------- Plan documents ---------- */}
                <Card data-testid="helper-docs">
                    <SectionTitle>Helper Insurance Plan options</SectionTitle>
                    <p className="text-sm text-navy-500 -mt-2 mb-4">
                        Download the Helper Insurance plan options and see what's inside.
                    </p>
                    <div className="grid sm:grid-cols-3 gap-4">
                        {PLAN_DOCS.map((doc) => (
                            <a
                                key={doc.file}
                                href={doc.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                data-testid={`helper-doc-${slug(doc.title)}`}
                                className="group rounded-xl border border-navy-100 bg-white p-4 flex items-start gap-3 hover:border-gold-500 hover:shadow-soft transition-all"
                            >
                                <FileText
                                    className="text-gold-600 shrink-0 mt-0.5"
                                    size={20}
                                />
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-navy-700 group-hover:text-navy-900">
                                        {doc.title}
                                    </div>
                                    <div className="text-xs text-navy-400 mt-0.5 truncate">
                                        {doc.caption}
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                </Card>

                {/* ---------- Options ---------- */}
                <Card>
                    <SectionTitle>Choose your cover</SectionTitle>

                    <p className="text-sm text-navy-500 -mt-2 mb-4">
                        Pick the plan that suits your home.
                    </p>

                    <div className="grid sm:grid-cols-2 gap-4">
                        {fields.map((f) => (
                            <FormField key={f.key} label={f.label} required>
                                <Select
                                    value={selections[f.key] || ""}
                                    onValueChange={(v) =>
                                        setSelections((s) => ({ ...s, [f.key]: v }))
                                    }
                                >
                                    <SelectTrigger data-testid={`helper-opt-${slug(f.key)}`}>
                                        <SelectValue
                                            placeholder={`Select ${f.label.toLowerCase()}`}
                                        />
                                    </SelectTrigger>

                                    <SelectContent>
                                        {visibleValues(f)
                                            .filter((v) => {
                                                if (f.label === "Choose your cover option") {
                                                    return !!PLAN_DOC_LOOKUP[v];
                                                }

                                                return true;
                                            })
                                            .map((v) => {
                                                const plan = PLAN_DOC_LOOKUP[v];

                                                return (
                                                    <SelectItem key={v} value={v}>
                                                        {f.label === "Choose your cover option"
                                                            ? plan?.title || v
                                                            : v}
                                                    </SelectItem>
                                                );
                                            })}
                                    </SelectContent>
                                </Select>
                            </FormField>
                        ))}
                    </div>
                </Card>

                {/* ---------- Price ---------- */}
                {complete && price?.priced && (
                    <div className="space-y-4" data-testid="helper-price">
                        <div className="grid sm:grid-cols-3 gap-4">
                            <PriceCard
                                label="Original Price"
                                value={price.original}
                                currency={price.currency}
                                testid="helper-original"
                            />
                            <PriceCard
                                label="Discounted Price"
                                value={price.discounted}
                                currency={price.currency}
                                testid="helper-discounted"
                                highlight
                            />
                            <PriceCard
                                label="Savings"
                                value={price.savings}
                                currency={price.currency}
                                testid="helper-savings"
                                tone="emerald"
                            />
                        </div>

                        <div className="rounded-2xl bg-white shadow-soft p-6 border border-navy-100/60 flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-3 text-sm text-navy-700">
                                <Sparkles className="text-gold-600 shrink-0" size={18} />
                                <div>
                                    <div className="font-semibold">
                                        {price.discount_pct != null
                                            ? `${price.discount_pct}% off the standard premium`
                                            : "Perx member pricing applied"}
                                    </div>
                                    <div className="text-navy-500 text-xs mt-0.5">
                                        {Object.values(price.selections || {}).join(" · ")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ---------- Application form ---------- */}
                {complete && price?.priced && !submitted && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            submit();
                        }}
                        noValidate
                        className="space-y-6"
                        data-testid="helper-form"
                    >
                        <Card>
                            <SectionTitle>Applicant / Employer details</SectionTitle>
                            <p className="text-sm text-navy-500 -mt-2 mb-5">
                                Tell us about you — the policyholder. Use the details exactly as they appear on your HKID.
                                <div className="text-[10px] uppercase tracking-[0.3em] text-gold-600">
                                    Fields marked * are mandatory.
                                </div>
                            </p>
                            
                                
                            
                            <FieldGrid>
                                <FormField label="Title" required error={errors.a_salutation}>
                                    <Select
                                        value={applicant.salutation || ""}
                                        onValueChange={(v) =>
                                            setApplicant((a) => ({ ...a, salutation: v }))
                                        }
                                    >
                                        <SelectTrigger data-testid="app-salutation">
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options.salutations.map((s) => (
                                                <SelectItem key={s} value={s}>
                                                    {s}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <TextInput
                                    label="First name"
                                    value={applicant.first_name}
                                    onChange={(v) => setApplicant((a) => ({ ...a, first_name: v }))}
                                    error={errors.a_first_name}
                                    testid="app-first-name"
                                />
                                <TextInput
                                    label="Last name"
                                    value={applicant.last_name}
                                    onChange={(v) => setApplicant((a) => ({ ...a, last_name: v }))}
                                    error={errors.a_last_name}
                                    testid="app-last-name"
                                />
                                <TextInput
                                    label="HKID number"
                                    value={applicant.hkid}
                                    onChange={(v) =>
                                        setApplicant((a) => ({ ...a, hkid: v.toUpperCase() }))
                                    }
                                    placeholder="A123456(7)"
                                    error={errors.a_hkid}
                                    testid="app-hkid"
                                />
                                <TextInput
                                    label="Phone number"
                                    value={applicant.telephone}
                                    onChange={(v) => setApplicant((a) => ({ ...a, telephone: v }))}
                                    error={errors.a_telephone}
                                    testid="app-telephone"
                                />
                                <TextInput
                                    label="Email address"
                                    type="email"
                                    value={applicant.email}
                                    onChange={(v) => setApplicant((a) => ({ ...a, email: v }))}
                                    error={errors.a_email}
                                    testid="app-email"
                                />
                                <TextInput
                                    label="Occupation"
                                    value={applicant.occupation}
                                    onChange={(v) => setApplicant((a) => ({ ...a, occupation: v }))}
                                    error={errors.a_occupation}
                                    testid="app-occupation"
                                />
                                <TextInput
                                    label="Home Address"
                                    value={applicant.residential_address}
                                    onChange={(v) =>
                                        setApplicant((a) => ({ ...a, residential_address: v }))
                                    }
                                    error={errors.a_residential_address}
                                    testid="app-address"
                                    className="sm:col-span-2"
                                />
                            </FieldGrid>
                        </Card>

                        <Card>
                            <SectionTitle>Helper details</SectionTitle>
                            <p className="text-sm text-navy-500 -mt-2 mb-5">
                            Now tell us about your helper (exactly as shown on their passport).
                            </p>
                            <FieldGrid>
                                <FormField label="Title" required error={errors.h_salutation}>
                                    <Select
                                        value={helper.salutation || ""}
                                        onValueChange={(v) =>
                                            setHelper((h) => ({ ...h, salutation: v }))
                                        }
                                    >
                                        <SelectTrigger data-testid="helper-salutation">
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options.salutations.map((s) => (
                                                <SelectItem key={s} value={s}>
                                                    {s}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <TextInput
                                    label="First name"
                                    value={helper.first_name}
                                    onChange={(v) => setHelper((h) => ({ ...h, first_name: v }))}
                                    error={errors.h_first_name}
                                    testid="helper-first-name"
                                />
                                <TextInput
                                    label="Last name"
                                    value={helper.last_name}
                                    onChange={(v) => setHelper((h) => ({ ...h, last_name: v }))}
                                    error={errors.h_last_name}
                                    testid="helper-last-name"
                                />
                                <FormField label="Nationality" required error={errors.h_nationality}>
                                    <Select
                                        value={helper.nationality || ""}
                                        onValueChange={(v) =>
                                            setHelper((h) => ({ ...h, nationality: v }))
                                        }
                                    >
                                        <SelectTrigger data-testid="helper-nationality">
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options.nationalities.map((n) => (
                                                <SelectItem key={n} value={n}>
                                                    {n}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormField>
                                <TextInput
                                    label="HKID number"
                                    value={helper.hk_card_no}
                                    placeholder="A123456(7)"
                                    onChange={(v) => setHelper((h) => ({ ...h, hk_card_no: v }))}
                                    error={errors.h_hk_card_no}
                                    testid="helper-hk-card"
                                />
                                <TextInput
                                    label="Passport number"
                                    value={helper.passport_no}
                                    onChange={(v) => setHelper((h) => ({ ...h, passport_no: v }))}
                                    error={errors.h_passport_no}
                                    testid="helper-passport"
                                />
                                <TextInput
                                    label="Date of Birth"
                                    type="date"
                                    value={helper.date_of_birth}
                                    onChange={(v) => setHelper((h) => ({ ...h, date_of_birth: v }))}
                                    error={errors.h_date_of_birth}
                                    testid="helper-dob"
                                />
                            </FieldGrid>
                        </Card>

                        <Card>
                            <SectionTitle>Insurance date</SectionTitle>
                            <p className="text-sm text-navy-500 -mt-2 mb-5">
                                Set the dates for your helper's employment and insurance.
                            </p>
                            <FieldGrid>
                                <TextInput
                                    label="Employment start date"
                                    type="date"
                                    value={insurance.contract_start_date}
                                    onChange={(v) =>
                                        setInsurance((s) => ({ ...s, contract_start_date: v }))
                                    }
                                    error={errors.i_contract_start_date}
                                    testid="ins-contract-start"
                                />
                                <TextInput
                                    label="Insurance start date"
                                    type="date"
                                    value={insurance.insurance_start_date}
                                    onChange={(v) =>
                                        setInsurance((s) => ({ ...s, insurance_start_date: v }))
                                    }
                                    error={errors.i_insurance_start_date}
                                    testid="ins-insurance-start"
                                />
                            </FieldGrid>

                            <div className="mt-6 space-y-6">
                                <YesNo
                                    question="Have you ever been declined, asked for special terms, or charged extra for domestic helper insurance?  "
                                    value={declined}
                                    onChange={setDeclined}
                                    error={errors.declined}
                                    testid="q-declined"
                                    details={declinedDetails}
                                    onDetails={setDeclinedDetails}
                                    detailsError={errors.declined_details}
                                />
                                <YesNo
                                    question="Have you made any domestic helper insurance claims in the last three years?  "
                                    value={claimed}
                                    onChange={setClaimed}
                                    error={errors.claimed}
                                    testid="q-claimed"
                                    details={claimedDetails}
                                    onDetails={setClaimedDetails}
                                    detailsError={errors.claimed_details}
                                />
                                <p className="text-sm text-navy-500 -mt-2 mb-5">
                                    Applicant Declaration.
                                </p>
                                <SimpleYes
                                    question="I understand this is an application for Helper Insurance through a third‑party insurance broker. My cover only starts once the application is processed, accepted, and the premium is paid. I’ll receive written confirmation when the policy is active."
                                    value={understand}
                                    onChange={setUnderstand}
                                    error={errors.understand}
                                    testid="q-understand"
                                />
                            </div>
                        </Card>

                        <div className="flex justify-end">
                            <Button
                                type="submit"
                                disabled={busy || pricing}
                                data-testid="helper-apply"
                                className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-8 font-semibold tracking-widest disabled:opacity-50"
                            >
                                {busy ? "SENDING…" : "APPLY"}
                            </Button>
                        </div>
                    </form>
                )}

                {/* ---------- Done ---------- */}
                {submitted && (
                    <Card>
                        <div className="flex gap-4 items-start" data-testid="helper-done">
                            <CheckCircle2 className="text-emerald-500 shrink-0" size={28} />
                            <div className="text-sm text-navy-700">
                                <div className="font-display text-xl text-navy-700 mb-1">
                                    Request sent
                                </div>
                                Your reference is{" "}
                                <span className="font-semibold">{submitted.display_id}</span>.{" "}
                                {vendor} will contact you to finalise cover at{" "}
                                {submitted.currency} {money(submitted.calculator_quote)}.
                            </div>
                        </div>
                    </Card>
                )}
            </div>
        </>
    );
}

/* ------------------------------ atoms ------------------------------ */

function Card({ children }) {
    return (
        <div className="rounded-2xl bg-white shadow-soft p-6 border border-navy-100/60">
            {children}
        </div>
    );
}

function SectionTitle({ children }) {
    return <h2 className="font-display text-xl text-navy-700 mb-4">{children}</h2>;
}

// function FormField({ label, required, children }) {
//     return (
//         <div>
//             <Label className="text-navy-700 uppercase tracking-widest text-xs">
//                 {label}
//                 {required && <span className="text-gold-600 ml-1">*</span>}
//             </Label>
//             <div className="mt-1.5">{children}</div>
//         </div>
//     );
// }

function PriceCard({ label, value, currency, testid, highlight, tone }) {
    const card = highlight
        ? "bg-gradient-to-br from-navy-700 to-navy-800 text-white border-navy-700"
        : tone === "emerald"
            ? "bg-emerald-50 border-emerald-200 text-navy-800"
            : "bg-white border-navy-100/60 text-navy-700";
    return (
        <div
            className={`rounded-2xl border shadow-soft p-5 ${card}`}
            data-testid={testid}
        >
            <div
                className={`text-[10px] uppercase tracking-[0.25em] ${highlight ? "text-gold-500" : "text-navy-500"
                    }`}
            >
                {label}
            </div>
            <div className="font-display text-3xl mt-2">
                <span className="text-sm align-top mr-1">{currency}</span>
                {money(value)}
            </div>
        </div>
    );
}

function Card2({ children, ...rest }) {
    return (
        <div
            className="rounded-2xl bg-white shadow-soft p-6 border border-navy-100/60"
            {...rest}
        >
            {children}
        </div>
    );
}

function FieldGrid({ children }) {
    return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}

function FormField({ label, required, error, children, className = "" }) {
    return (
        <div className={className} data-error={error ? "true" : undefined}>
            <Label className="text-navy-700 uppercase tracking-widest text-xs">
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            <div className="mt-1.5">{children}</div>
            {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
        </div>
    );
}

function TextInput({ label, value, onChange, error, type = "text", placeholder, testid, className }) {
    return (
        <FormField label={label} required error={error} className={className}>
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full h-10 rounded-md border border-navy-100 bg-white px-3 text-sm text-navy-700 focus:outline-none focus:ring-2 focus:ring-gold-400"
            />
        </FormField>
    );
}

function YesNo({ question, value, onChange, error, testid, details, onDetails, detailsError }) {
    return (
        <div data-error={error ? "true" : undefined}>
            <div className="text-sm text-navy-700">{question}</div>
            <div className="flex gap-6 mt-3">
                {["yes", "no"].map((opt) => (
                    <label key={opt} className="flex items-center gap-2 text-sm text-navy-700">
                        <input
                            type="radio"
                            name={testid}
                            checked={value === opt}
                            onChange={() => onChange(opt)}
                            data-testid={`${testid}-${opt}`}
                            className="h-4 w-4 accent-navy-700"
                        />
                        {opt === "yes" ? "Yes" : "No"}
                    </label>
                ))}
            </div>
            {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
            {value === "yes" && (
                <>
                    <textarea
                        value={details}
                        onChange={(e) => onDetails(e.target.value)}
                        rows={4}
                        maxLength={2000}
                        data-testid={`${testid}-details`}
                        placeholder="Please give full details"
                        className="mt-3 w-full rounded-md border border-navy-100 bg-white p-3 text-sm text-navy-700 focus:outline-none focus:ring-2 focus:ring-gold-400"
                    />
                    {detailsError && (
                        <div className="text-xs text-red-600 mt-1">{detailsError}</div>
                    )}
                </>
            )}
        </div>
    );
}


function SimpleYes({ question, value, onChange, error, testid}) {
    return (
        <div data-error={error ? "true" : undefined}>
            <div className="text-sm text-navy-700">{question}</div>
            <div className="flex gap-6 mt-3">
                {["yes", "no"].map((opt) => (
                    <label key={opt} className="flex items-center gap-2 text-sm text-navy-700">
                        <input
                            type="radio"
                            name={testid}
                            checked={value === opt}
                            onChange={() => onChange(opt)}
                            data-testid={`${testid}-${opt}`}
                            className="h-4 w-4 accent-navy-700"
                        />
                        {opt === "yes" ? "Yes" : "No"}
                    </label>
                ))}
            </div>
            {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
        </div>
    );
}
